<template>
  <div class="container mt-5">
    <div class="card modern-card">
      <div class="card-header text-center">
        <i v-if="verificationStatus === 'success'" class="fas fa-check-circle fa-3x text-white mb-3"></i>
        <i v-else-if="verificationStatus === 'error'" class="fas fa-times-circle fa-3x text-white mb-3"></i>
        <i v-else class="fas fa-spinner fa-spin fa-3x text-white mb-3"></i>
        
        <h3 class="mb-0">
          {{ verificationStatus === 'success' ? ' Paiement réussi !' : 
             verificationStatus === 'error' ? ' Erreur de paiement' : 
             ' Vérification en cours...' }}
        </h3>
      </div>
      
      <div class="card-body p-4">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary mb-3"></div>
          <p class="text-muted">Vérification du paiement et mise à jour du statut...</p>
        </div>

        <div v-else-if="verificationStatus === 'success'" class="success-content">
          <div class="alert alert-success d-flex align-items-center mb-4">
            <i class="fas fa-info-circle me-2"></i>
            <div>
              <strong>Votre paiement a été confirmé avec succès !</strong>
              <br>
              Votre dossier BESC est maintenant en attente de validation par le Conseil des Chargeurs.
            </div>
          </div>

          <div class="payment-details">
            <h5 class="mb-3">Détails de la transaction</h5>
            <div class="detail-row">
              <span class="detail-label">Référence BESC</span>
              <span class="detail-value">{{ refbesc }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Statut du dossier</span>
              <span class="badge bg-warning">{{ bescStatus }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Méthode de paiement</span>
              <span class="detail-value text-capitalize">{{ paymentMethod }}</span>
            </div>
            <div class="detail-row" v-if="transactionId">
              <span class="detail-label">ID Transaction</span>
              <span class="detail-value">{{ transactionId }}</span>
            </div>
          </div>

          <div class="action-buttons mt-4">
            <button @click="printReceipt" class="btn btn-outline-primary me-2">
              <i class="fas fa-print me-2"></i>
              Imprimer le reçu
            </button>
            <router-link to="/liste-besc" class="btn btn-primary">
              <i class="fas fa-list me-2"></i>
              Retour à la liste
            </router-link>
          </div>
        </div>

        <div v-else-if="verificationStatus === 'error'" class="error-content">
          <div class="alert alert-danger">
            <i class="fas fa-exclamation-triangle me-2"></i>
            <strong>Erreur :</strong> {{ errorMessage }}
          </div>
          <div class="text-center mt-4">
            <router-link to="/liste-besc" class="btn btn-secondary">
              <i class="fas fa-arrow-left me-2"></i>
              Retour à la liste
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error as showError, success as showSuccess } from '@/utils/utils';

export default defineComponent({
  name: 'PaiementSuccess',
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    const loading = ref(true);
    const verificationStatus = ref<'loading' | 'success' | 'error'>('loading');
    const refbesc = ref('');
    const bescStatus = ref('');
    const paymentMethod = ref('');
    const transactionId = ref('');
    const errorMessage = ref('');

    const capturePayPalPayment = async (bescId: string, token: string) => {
      try {
        console.log('Capture du paiement PayPal...');
        console.log('Token:', token);

        const response = await ApiService.post(
          `/besc/${bescId}/payment/paypal/capture`,  
          { orderID: token }
        );

        console.log('Reponse capture:', response.data);
        
        if (response.data.data) {
          showSuccess('Paiement PayPal confirme !');
          return true;
        }
        
        return false;
      } catch (err: any) {
        console.error('Erreur capture PayPal:', err);
        throw new Error(err.response?.data?.message || 'Echec de la capture du paiement');
      }
    };

    const verifyKKiaPayPayment = async (bescId: string, transactionId: string) => {
      try {
        console.log(' Vérification paiement KKiaPay...');
        console.log(' Transaction ID:', transactionId);
        
        const response = await ApiService.post(
          `/besc/${bescId}/payment/kkiapay/verify`,
          { transactionId }
        );

        console.log(' Réponse vérification:', response.data);
        
        if (response.data.data?.paymentConfirmed) {
          showSuccess('Paiement KKiaPay confirmé !');
          return true;
        }
        
        return false;
      } catch (err: any) {
        console.error(' Erreur vérification KKiaPay:', err);
        throw new Error(err.response?.data?.message || 'Échec de la vérification du paiement');
      }
    };

    const verifyPaymentAndBesc = async () => {
      try {
        const bescId = route.query.bescId as string;
        const method = route.query.method as string;
        
        console.log(' Vérification du BESC:', bescId);
        console.log(' Méthode:', method);

        if (method === 'paypal') {
          console.log(' Détection d\'un retour PayPal, capture du paiement...');
          const token = route.query.token as string;
          
          if (!token) {
            throw new Error('Token PayPal manquant dans l\'URL');
          }

          await capturePayPalPayment(bescId, token);
          transactionId.value = token;
        } 
        else if (method === 'kkiapay') {
          console.log(' Détection d\'un retour KKiaPay, vérification...');
          const txId = route.query.transaction_id as string;
          
          if (!txId) {
            throw new Error('ID de transaction KKiaPay manquant dans l\'URL');
          }

          await verifyKKiaPayPayment(bescId, txId);
          transactionId.value = txId;
        }

        // Charger les infos du BESC
        const { data } = await ApiService.get(`/besc/${bescId}`);
        
        console.log(' BESC récupéré:', data.data);

        refbesc.value = data.data.refbesc;
        bescStatus.value = data.data.currentStatus?.label || 'En attente';
        paymentMethod.value = method;

        verificationStatus.value = 'success';
        loading.value = false;

      } catch (err: any) {
        console.error(' Erreur:', err);
        verificationStatus.value = 'error';
        errorMessage.value = err.message || 'Une erreur est survenue lors de la vérification du paiement';
        loading.value = false;
      }
    };

    const printReceipt = () => {
      window.print();
    };

    onMounted(() => {
      console.log(' Page de succès chargée');
      console.log(' Query params:', route.query);
      verifyPaymentAndBesc();
    });

    return {
      loading,
      verificationStatus,
      refbesc,
      bescStatus,
      paymentMethod,
      transactionId,
      errorMessage,
      printReceipt
    };
  }
});
</script>

<style scoped>
.modern-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-width: 600px;
  margin: 0 auto;
}

.card-header {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 2rem;
}

.success-content, .error-content {
  padding: 1rem 0;
}

.payment-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid #28a745;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e9ecef;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: #6c757d;
  font-weight: 500;
}

.detail-value {
  color: #2c3e50;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

@media print {
  .action-buttons, .card-header i {
    display: none !important;
  }
}
</style>